import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { FaArrowLeft, FaBell, FaPlus, FaEye, FaTrash, FaSearch, FaBroadcastTower } from 'react-icons/fa';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';

const AdminNotifications = () => {
  const { user } = useSelector((state) => state.auth);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastData, setBroadcastData] = useState({ title: '', message: '', type: 'info' });
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [dismissedNotifications, setDismissedNotifications] = useState(() => {
    const dismissed = localStorage.getItem('dismissed_admin_notifications');
    return dismissed ? JSON.parse(dismissed) : [];
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      // For now, use existing logs endpoint - in future this could be specific notification endpoint
      const response = await adminAPI.getLogs({ limit: 100 });
      const notificationLogs = response.data.filter(log =>
        log.level === 'info' || log.level === 'warning' || log.message.includes('notification')
      );
      setNotifications(notificationLogs);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendBroadcast = async () => {
    if (!broadcastData.title || !broadcastData.message) return;

    try {
      // This would need a backend endpoint to send broadcast notifications
      // For now, just show success message
      toast.success('Notification broadcast envoyée avec succès!');
      setShowBroadcastModal(false);
      setBroadcastData({ title: '', message: '', type: 'info' });
    } catch (error) {
      console.error('Error sending broadcast:', error);
      toast.error('Erreur lors de l\'envoi de la notification broadcast');
    }
  };

  const handleViewNotification = (notification) => {
    setSelectedNotification(notification);
    setShowDetailsModal(true);
  };

  const handleDeleteNotification = (notification) => {
    setNotificationToDelete(notification);
    setShowDeleteModal(true);
  };

  const confirmDeleteNotification = async () => {
    if (!notificationToDelete) return;

    try {
      if (user.role === 'superadmin') {
        // Superadmin: permanently delete from database
        await adminAPI.deleteLog(notificationToDelete.id);
        toast.success('Notification supprimée définitivement!');
      } else {
        // Regular admin: hide locally
        const newDismissed = [...dismissedNotifications, notificationToDelete.id];
        setDismissedNotifications(newDismissed);
        localStorage.setItem('dismissed_admin_notifications', JSON.stringify(newDismissed));
        toast.success('Notification masquée avec succès!');
      }

      // Remove from current notifications list
      setNotifications(notifications.filter(n => n.id !== notificationToDelete.id));
      setShowDeleteModal(false);
      setNotificationToDelete(null);
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Filter notifications to exclude dismissed ones (only for regular admins)
  const activeNotifications = user.role === 'superadmin'
    ? notifications
    : notifications.filter(notification => !dismissedNotifications.includes(notification.id));

  const filteredNotifications = activeNotifications.filter(notification => {
    const matchesSearch = notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || notification.level === filterType;
    return matchesSearch && matchesType;
  });

  // Check if user is admin or superadmin
  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return <Navigate to="/" replace />;
  }

  const isSuperAdmin = user.role === 'superadmin';

  return (
    <div className="admin-notifications min-vh-100" style={{ backgroundColor: 'var(--lova-bg-secondary)' }}>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-outline-secondary me-3"
                  onClick={() => window.location.href = '/admin/dashboard'}
                >
                  <FaArrowLeft className="me-2" />
                  Retour
                </button>
                <div>
                  <h1 className="h2 mb-1">Gestion des Notifications</h1>
                  <p className="mb-0" style={{ color: 'var(--lova-text-secondary)' }}>
                    {isSuperAdmin
                      ? 'Gérer et supprimer définitivement les notifications système'
                      : 'Gérer les notifications système et envoyer des broadcasts'
                    }
                  </p>
                </div>
              </div>
              {!isSuperAdmin && (
                <button
                  className="btn btn-primary"
                  onClick={() => setShowBroadcastModal(true)}
                >
                  <FaBroadcastTower className="me-2" />
                  Envoyer Broadcast
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row mb-4">
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaBell className="text-primary mb-2" size={24} />
                <h4 className="h5 mb-1">{activeNotifications.length}</h4>
                <small style={{ color: 'var(--lova-text-secondary)' }}>Total Notifications</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaBell className="text-info mb-2" size={24} />
                <h4 className="h5 mb-1">{activeNotifications.filter(n => n.level === 'info').length}</h4>
                <small className="text-muted">Informations</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                <FaBell className="text-warning mb-2" size={24} />
                <h4 className="h5 mb-1">{activeNotifications.filter(n => n.level === 'warning').length}</h4>
                <small className="text-muted">Avertissements</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center">
                {isSuperAdmin ? (
                  <>
                    <FaTrash className="text-danger mb-2" size={24} />
                    <h4 className="h5 mb-1">{notifications.length - activeNotifications.length}</h4>
                    <small className="text-muted">Supprimées</small>
                  </>
                ) : (
                  <>
                    <FaBroadcastTower className="text-success mb-2" size={24} />
                    <h4 className="h5 mb-1">0</h4>
                    <small className="text-muted">Broadcasts Envoyés</small>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-text"><FaSearch /></span>
              <input
                type="text"
                className="form-control"
                placeholder="Rechercher dans les notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">Tous les types</option>
              <option value="info">Information</option>
              <option value="warning">Avertissement</option>
              <option value="error">Erreur</option>
            </select>
          </div>
          <div className="col-md-3">
            <div className="d-flex align-items-center h-100">
              <small className="text-muted">{filteredNotifications.length} notification(s)</small>
            </div>
          </div>
        </div>

        {/* Notifications Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th className="border-0 ps-4">Type</th>
                          <th className="border-0">Niveau</th>
                          <th className="border-0">Message</th>
                          <th className="border-0">Utilisateur</th>
                          <th className="border-0">Date/Heure</th>
                          <th className="border-0 pe-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredNotifications.map((notification) => (
                          <tr key={notification.id}>
                            <td className="ps-4">
                              <FaBell className={`text-${notification.level === 'error' ? 'danger' : notification.level === 'warning' ? 'warning' : 'info'}`} />
                            </td>
                            <td>
                              <span className={`badge bg-${notification.level === 'error' ? 'danger' : notification.level === 'warning' ? 'warning' : 'info'}`}>
                                {notification.level}
                              </span>
                            </td>
                            <td style={{ maxWidth: '400px' }}>
                              <div style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                {notification.message}
                              </div>
                            </td>
                            <td>{notification.user || 'Système'}</td>
                            <td className="pe-4">
                              <small className="text-muted">{notification.time}</small>
                            </td>
                            <td className="pe-4">
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-outline-primary"
                                  title="Voir détails"
                                  onClick={() => handleViewNotification(notification)}
                                >
                                  <FaEye />
                                </button>
                                <button
                                  className={`btn ${isSuperAdmin ? 'btn-outline-danger' : 'btn-outline-warning'}`}
                                  title={isSuperAdmin ? 'Supprimer définitivement' : 'Masquer'}
                                  onClick={() => handleDeleteNotification(notification)}
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {filteredNotifications.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center py-4 text-muted">
                              Aucune notification trouvée
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Broadcast Modal - Only for regular admins */}
      {!isSuperAdmin && showBroadcastModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Envoyer une Notification Broadcast</h5>
                <button type="button" className="btn-close" onClick={() => setShowBroadcastModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Titre</label>
                  <input
                    type="text"
                    className="form-control"
                    value={broadcastData.title}
                    onChange={(e) => setBroadcastData({...broadcastData, title: e.target.value})}
                    placeholder="Titre de la notification"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Message</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={broadcastData.message}
                    onChange={(e) => setBroadcastData({...broadcastData, message: e.target.value})}
                    placeholder="Contenu de la notification"
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label">Type</label>
                  <select
                    className="form-select"
                    value={broadcastData.type}
                    onChange={(e) => setBroadcastData({...broadcastData, type: e.target.value})}
                  >
                    <option value="info">Information</option>
                    <option value="warning">Avertissement</option>
                    <option value="success">Succès</option>
                    <option value="error">Erreur</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowBroadcastModal(false)}>
                  Annuler
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSendBroadcast}>
                  Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedNotification && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Détails de la Notification</h5>
                <button type="button" className="btn-close" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>Informations</h6>
                    <p><strong>Niveau:</strong> <span className={`badge bg-${selectedNotification.level === 'error' ? 'danger' : selectedNotification.level === 'warning' ? 'warning' : 'info'}`}>{selectedNotification.level}</span></p>
                    <p><strong>Utilisateur:</strong> {selectedNotification.user || 'Système'}</p>
                    <p><strong>Date/Heure:</strong> {selectedNotification.time}</p>
                  </div>
                  <div className="col-md-6">
                    <h6>Message</h6>
                    <div className="border p-3 rounded" style={{ backgroundColor: 'var(--lova-bg-secondary)', minHeight: '100px' }}>
                      {selectedNotification.message}
                    </div>
                  </div>
                </div>
                {selectedNotification.details && (
                  <div className="mt-3">
                    <h6>Détails supplémentaires</h6>
                    <pre className="border p-3 rounded" style={{ backgroundColor: 'var(--lova-bg-secondary)', fontSize: '0.875rem' }}>
                      {JSON.stringify(selectedNotification.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && notificationToDelete && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isSuperAdmin ? 'Confirmer la suppression définitive' : 'Confirmer le masquage'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>
                  {isSuperAdmin
                    ? 'Êtes-vous sûr de vouloir supprimer définitivement cette notification ?'
                    : 'Êtes-vous sûr de vouloir masquer cette notification ?'
                  }
                </p>
                <div className={`alert ${isSuperAdmin ? 'alert-danger' : 'alert-info'}`}>
                  <strong>Message:</strong> {notificationToDelete.message}
                </div>
                <p className="text-muted mb-0">
                  {isSuperAdmin
                    ? 'Cette action est irréversible et supprimera définitivement la notification de la base de données.'
                    : 'La notification sera masquée pour vous mais restera visible pour les autres administrateurs.'
                  }
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Annuler
                </button>
                <button
                  type="button"
                  className={`btn ${isSuperAdmin ? 'btn-danger' : 'btn-warning'}`}
                  onClick={confirmDeleteNotification}
                >
                  <FaTrash className="me-2" />
                  {isSuperAdmin ? 'Supprimer définitivement' : 'Masquer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .min-vh-100 {
          min-height: 100vh;
        }
        .table th {
          font-weight: 600 !important;
          font-size: 0.875rem !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          background: var(--lova-bg-secondary) !important;
          color: var(--lova-text-primary) !important;
          border-bottom: 2px solid var(--lova-gray-light) !important;
        }
        .table td {
          vertical-align: middle !important;
          background: var(--lova-bg-primary) !important;
          color: var(--lova-text-primary) !important;
        }
        .table tbody tr {
          background: var(--lova-bg-primary) !important;
        }
        .table tbody tr:hover {
          background: var(--lova-bg-secondary) !important;
        }
        .modal-content {
          background: var(--lova-bg-primary) !important;
          color: var(--lova-text-primary) !important;
        }
        .modal-header, .modal-footer {
          border-color: var(--lova-gray-light) !important;
        }
      `}</style>
    </div>
  );
};

export default AdminNotifications;
